package com.exura.components.layouts.nav

import com.exura.primary
import com.exura.ui.Dimens
import com.exura.ui.Durations
import com.exura.util.ariaCurrent
import com.exura.warning
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderRight
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.selectors.link
import com.varabyte.kobweb.silk.style.selectors.visited
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times

sealed interface NavTextKind : ComponentKind
sealed interface NavNavigableElementKind : ComponentKind
sealed interface NavigationKind : ComponentKind

val NavTextStyle = CssStyle<NavTextKind> {
    val palette = colorMode.toPalette()

    base {
        Modifier
            .color(palette.background)
            .transition(Transition.all(Durations.short))
            .whiteSpace(WhiteSpace.NoWrap)
            .overflow(Overflow.Hidden)
            .textOverflow(TextOverflow.Ellipsis)
            .opacity(1)
            .maxWidth(Dimens.navNavigationElementContentSize)
    }
}

val NavTextCollapsedVariant = NavTextStyle.addVariant {
    base {
        Modifier
            .opacity(0)
            .maxWidth(Dimens.spacingNone)
    }
}


val NavTextCollapsedVisibleVariant = NavTextStyle.addVariant {
    val maxContentSize = Dimens.navElementIndent + 2 * Dimens.iconRelSizeDefault + Dimens.iconTextRelGapSizeDefault

    base {
        Modifier
            .opacity(1)
            .maxWidth(maxContentSize)
    }
}

val NavNavigableElementStyle = CssStyle<NavNavigableElementKind> {
    val palette = colorMode.toPalette()
    val textBase = Modifier
        .color(palette.background)
        .textDecorationLine(TextDecorationLine.None)
        .fontSize(Dimens.navElementFontSize)

    val highlightedBgColor = palette.background.toRgb().copyf(alpha = 0.2f)
    val elementHighlighted = Modifier.backgroundColor(highlightedBgColor)


    base {
        textBase
            .width(100.percent)
            .display(DisplayStyle.Flex)
            .transition(Transition.all(Durations.short))
            .textOverflow(TextOverflow.Ellipsis)
            .padding(leftRight = Dimens.navElementIndent, topBottom = Dimens.navElementWrapping)
            .cursor(Cursor.Pointer)
            .borderRadius(Dimens.navElementBorderRadius)
    }
    palette.primary.lightened()
    ariaCurrent {
        textBase
            .then(elementHighlighted)
            .fontWeight(FontWeight.Medium)
    }

    hover {
        textBase
            .then(elementHighlighted)
            .fontWeight(FontWeight.Bold)
    }

    link { textBase }
    visited { textBase }
}

val NavNavigableElementWarnVariant = NavNavigableElementStyle.addVariant {
    val palette = colorMode.toPalette()
    val highlightedBgColor = palette.warning.toRgb().copyf(alpha = 0.8f)
    hover {
        Modifier
            .backgroundColor(highlightedBgColor)
    }
}

val NavSectionHeaderStyle = CssStyle {
    val palette = colorMode.toPalette()

    base {
        Modifier
            .fillMaxWidth()
            .transition(Transition.all(Durations.short))
            .borderBottom(width = Dimens.navSectionSeparatorWidth, style = LineStyle.Solid, color = palette.background)
            .color(palette.background)
            .padding(topBottom = Dimens.navSectionHeaderWrapping)
            .fontWeight(FontWeight.Medium)
            .fontSize(Dimens.navSectionHeaderFontSize)
    }
}

val NavStyle = CssStyle<NavigationKind> {
    val palette = colorMode.toPalette()
    val maxContentSize = 2 * Dimens.navElementIndent +
            2 * Dimens.iconRelSizeDefault +
            Dimens.iconTextRelGapSizeDefault +
            Dimens.navNavigationElementContentSize
    base {
        Modifier
            .transition(Transition.all(Durations.short))
            .fillMaxHeight()
            .padding(leftRight = Dimens.navNavigationIndent, topBottom = Dimens.navNavigationWrapping)
            .backgroundColor(palette.primary)
            .width(2 * Dimens.navNavigationIndent + maxContentSize)
            .flexShrink(0)
            .borderRight(width = Dimens.navNavigationSeparatorWidth, style = LineStyle.Solid, color = palette.border)
    }
}

val NavStyleCollapsed = NavStyle.addVariant {
    val maxContentSize = 2 * Dimens.navElementIndent + 2 * Dimens.iconRelSizeDefault + 2 * Dimens.iconTextRelGapSizeDefault
    base {
        Modifier
            .width(2 * Dimens.navNavigationIndent + maxContentSize)
    }
}
